import Usercentrics from "@usercentrics/cmp-browser-sdk";
import { useContext, useEffect, useMemo, useState } from "react";
import { DecisionsProvider, Status } from "./contexts/decisionContext";
import FirstLayer from "./layers/FirstLayer";
import SecondLayer from "./layers/SecondLayer";
import { UsercentricsContext } from "./UsercentricsProvider";

export enum UI_LAYER {
  FIRST_LAYER = 0,
  NONE = 1,
  PRIVACY_BUTTON = 2,
  SECOND_LAYER = 3,
}
/** All the different UI variants  */
enum UI_VARIANT {
  CCPA = 0,
  DEFAULT = 1,
  TCF = 2,
}

const UsercentricsCMP = ({ settingsId }) => {
  const UC = useMemo(
    () => new Usercentrics(settingsId, { createTcfApiStub: true }),
    [settingsId]
  );
  (window as any).UC = UC;
  const [view, setView] = useState(UI_LAYER.NONE);
  const [settings, setSettings] = useState<any>(null);
  const [labels, setLabels] = useState<any>(null);
  const { categories, setCategories } = useContext(UsercentricsContext);
  const [status, setStatus] = useState<Status>("initializing");

  useEffect(() => {
    UC.init().then((initialUIValues) => {
      (window as any).UC_SET_VIEW = setView;

      // getSettingsCore() and getSettingsLabels() returns all Usercentrics settings you need for your custom solution
      setSettings(UC.getSettingsCore());
      setLabels(UC.getSettingsLabels());
      UC.getCategoriesFullInfo().then((data) => {
        setCategories(data);
        setStatus("initialized");
      });

      // getCategories() returns all categories' and data processing services' information
      // const categories = UC.getCategories();

      if ((initialUIValues.variant as any) === UI_VARIANT.DEFAULT) {
        if ((initialUIValues.initialLayer as any) === UI_LAYER.FIRST_LAYER) {
          setView(UI_LAYER.FIRST_LAYER);
        } else if (
          (initialUIValues.initialLayer as any) === UI_LAYER.PRIVACY_BUTTON
        ) {
          setView(UI_LAYER.PRIVACY_BUTTON);
        } else {
          setView(UI_LAYER.NONE);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UC]);

  const onAcceptAllHandler = () => {
    UC.acceptAllServices().then(() => {
      onUpdateViewHandler(UI_LAYER.PRIVACY_BUTTON);
    });
  };

  const onDenyAllHandler = () => {
    UC.denyAllServices().then(() => {
      // Remember to fetch the now updated categories
      // const categories = UC.getCategories();
      // setCategories(categories);
      onUpdateViewHandler(UI_LAYER.PRIVACY_BUTTON);
    });
  };

  const onTCFUICloseHandler = (): void => {
    UC.setTCFUIAsClosed().then(() => {
      onUpdateViewHandler(UI_LAYER.PRIVACY_BUTTON);
    });
  };

  const onUpdateViewHandler = (layer) => {
    setView(layer);
    UC.updateLayer(layer).then(() => {
      setSettings(UC.getSettingsCore());
      setLabels(UC.getSettingsLabels());
      UC.getCategoriesFullInfo().then((data) => setCategories(data));
    });
  };

  switch (view) {
    case UI_LAYER.FIRST_LAYER:
      UC.setTCFUIAsOpen();
      return (
        <DecisionsProvider categories={categories} status={status}>
          <FirstLayer
            labels={labels}
            onTCFUICloseHandler={onTCFUICloseHandler}
            onUpdateViewHandler={onUpdateViewHandler}
            onAcceptAllHandler={onAcceptAllHandler}
          />
        </DecisionsProvider>
      );
    case UI_LAYER.SECOND_LAYER:
      return (
        <DecisionsProvider categories={categories} status={status}>
          <SecondLayer
            categories={categories}
            labels={labels}
            onTCFUICloseHandler={onTCFUICloseHandler}
            onAcceptAllHandler={onAcceptAllHandler}
            onUpdateViewHandler={onUpdateViewHandler}
            UC={UC}
          />
        </DecisionsProvider>
      );
    case UI_LAYER.PRIVACY_BUTTON:
    case UI_LAYER.NONE:
    default:
      return null;
  }
};

export default UsercentricsCMP;
